import http from './client';
import { errorHandler, parseApiError } from './errorHandler';
import type { ApiResponse } from './types';

export interface OwnedTeam {
  id: string;
  name: string;
  description: string;
  ownerUserId: string;
  ownerName: string;
}

export interface MemberTeam {
  teamId: string;
  name: string;
  description: string;
  ownerUserId: string;
  ownerName: string;
  /** Team tier: `member` until staff-type roles are added */
  teamMembershipRole: string;
  joinedAt: string;
}

export interface TeamMemberRow {
  userId: string;
  name: string;
  email: string;
  avatar?: string;
  teamMembershipRole: string;
  accountTypeLabel: string;
  appRole: string | null;
  joinedAt: string;
}

export interface TeamInviteOutgoing {
  id: string;
  email: string;
  status?: string;
  teamMembershipRole: string;
  inviteeAccountTypeLabel: string;
  inviteeAppRole: string | null;
  createdAt?: string;
  expiresAt: string;
}

export interface TeamInviteIncoming {
  id: string;
  teamName: string;
  ownerName: string;
  email: string;
  teamMembershipRole: string;
  expiresAt: string;
}

export interface TeamDraftRow {
  id: string;
  uuid: string;
  title: string;
  internalName: string;
  createdByUserId: string | null;
  createdByName: string;
  status?: string;
  updatedAt: string;
}

export const teamApi = {
  getMyTeamContext: async (): Promise<
    ApiResponse<{ ownedTeam: OwnedTeam | null; memberTeams: MemberTeam[] }>
  > => {
    try {
      const response = await http.get('/team/my');
      const d = response.data as {
        ownedTeam?: OwnedTeam | null;
        memberTeams?: MemberTeam[];
      };
      return {
        success: true,
        data: {
          ownedTeam: d?.ownedTeam ?? null,
          memberTeams: d?.memberTeams ?? [],
        },
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  createTeam: async (payload: {
    name: string;
    description?: string | null;
  }): Promise<ApiResponse<{ team: OwnedTeam }>> => {
    try {
      const response = await http.post('/team', payload);
      const body = response.data as { team: OwnedTeam };
      return { success: true, data: { team: body.team } };
    } catch (error) {
      const { message, fieldErrors } = parseApiError(error);
      return { success: false, error: message, fieldErrors };
    }
  },

  updateTeam: async (payload: {
    name: string;
    description?: string | null;
  }): Promise<ApiResponse<{ success: boolean }>> => {
    try {
      const response = await http.put('/team', payload);
      return { success: true, data: response.data as { success: boolean } };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  dissolveTeam: async (): Promise<ApiResponse<null>> => {
    try {
      await http.delete('/team');
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  sendInvite: async (payload: {
    email: string;
  }): Promise<ApiResponse<{ sentTo: string; signupRequired?: boolean }>> => {
    try {
      const response = await http.post('/team/invite', payload);
      return {
        success: true,
        data: response.data as { sentTo: string; signupRequired?: boolean },
      };
    } catch (error) {
      const { message } = parseApiError(error);
      return { success: false, error: message };
    }
  },

  verifyInvite: async (
    token: string,
  ): Promise<
    ApiResponse<{
      teamName: string;
      ownerName: string;
      inviteEmail: string;
      role: string;
    }>
  > => {
    try {
      const response = await http.post('/team/invite/verify', { token });
      const body = response.data as {
        data?: {
          teamName: string;
          ownerName: string;
          inviteEmail: string;
          role: string;
        };
      };
      return { success: true, data: body.data as never };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  acceptInvite: async (token: string): Promise<ApiResponse<null>> => {
    try {
      await http.post('/team/invite/accept', { token });
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  declineInvite: async (token: string): Promise<ApiResponse<null>> => {
    try {
      await http.post('/team/invite/decline', { token });
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** Accept pending invite while logged in (matches email; no email link token). */
  acceptInviteById: async (
    invitationId: string,
  ): Promise<ApiResponse<null>> => {
    try {
      await http.post(`/team/invitations/${invitationId}/accept`);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  declineInviteById: async (
    invitationId: string,
  ): Promise<ApiResponse<null>> => {
    try {
      await http.post(`/team/invitations/${invitationId}/decline`);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getIncomingInvites: async (): Promise<
    ApiResponse<{ invitations: TeamInviteIncoming[] }>
  > => {
    try {
      const response = await http.get('/team/invitations/incoming');
      const body = response.data as { invitations?: TeamInviteIncoming[] };
      return {
        success: true,
        data: { invitations: body.invitations ?? [] },
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getOutgoingInvites: async (): Promise<
    ApiResponse<{ invitations: TeamInviteOutgoing[] }>
  > => {
    try {
      const response = await http.get('/team/invitations/outgoing');
      const body = response.data as { invitations?: TeamInviteOutgoing[] };
      return {
        success: true,
        data: { invitations: body.invitations ?? [] },
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  /** Owner revokes a pending invite they sent. */
  revokeInvite: async (invitationId: string): Promise<ApiResponse<null>> => {
    try {
      await http.delete(`/team/invitations/${invitationId}`);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  listMembers: async (): Promise<ApiResponse<{ members: TeamMemberRow[] }>> => {
    try {
      const response = await http.get('/team/members');
      const body = response.data as { members?: TeamMemberRow[] };
      return {
        success: true,
        data: { members: body.members ?? [] },
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  removeMember: async (userId: string): Promise<ApiResponse<null>> => {
    try {
      await http.delete(`/team/members/${userId}`);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  leaveTeam: async (teamId: string): Promise<ApiResponse<null>> => {
    try {
      await http.post(`/team/leave/${teamId}`);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getDrafts: async (): Promise<ApiResponse<{ drafts: TeamDraftRow[] }>> => {
    try {
      const response = await http.get('/team/drafts');
      const body = response.data as { drafts?: TeamDraftRow[] };
      return {
        success: true,
        data: { drafts: body.drafts ?? [] },
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  publishDraft: async (recipeId: string): Promise<ApiResponse<null>> => {
    try {
      await http.post(`/team/drafts/${recipeId}/publish`);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
};
